import { useEffect, useState } from 'react'
import { MapPin, Search, X } from 'lucide-react'
import { API_CONFIG } from '../../config/settings'
import {
  fetchPlacePredictions,
  isGoogleMapsConfigured,
  PlacePrediction,
  resolveSearchCoordinates,
  SelectedLocation,
} from '../../lib/googleMaps'

export interface LocationOption {
  name: string
  description?: string
}

interface LocationModalProps {
  isOpen: boolean
  value: string
  onClose: () => void
  onSelect: (location: SelectedLocation) => void
  prefetched?: LocationOption[]
}

const LocationModal = ({ isOpen, value, onClose, onSelect, prefetched }: LocationModalProps) => {
  const [query, setQuery] = useState(value || '')
  const [locations, setLocations] = useState<LocationOption[]>(prefetched || [])
  const [loading, setLoading] = useState(false)
  const [placesLoading, setPlacesLoading] = useState(false)
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([])
  const [placesError, setPlacesError] = useState<string | null>(null)
  const [resolvingPick, setResolvingPick] = useState(false)

  useEffect(() => {
    if (!isOpen || (prefetched && prefetched.length > 0)) return
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOCATIONS}`)
        const data = await res.json()
        if (cancelled) return
        if (data.success && data.locations) {
          setLocations(
            data.locations.map((loc: { location?: string; city?: string; state?: string; country?: string }) => ({
              name: loc.location || `${loc.city}, ${loc.state}`,
              description: loc.country || 'Available location',
            })),
          )
        }
      } catch {
        if (!cancelled) setLocations([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [isOpen, prefetched])

  useEffect(() => {
    if (isOpen) {
      setQuery(value || '')
      setPlacePredictions([])
      setPlacesError(null)
    }
  }, [isOpen, value])

  useEffect(() => {
    if (!isOpen) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const trimmed = query.trim()
    if (trimmed.length < 2 || !isGoogleMapsConfigured()) {
      setPlacePredictions([])
      setPlacesLoading(false)
      setPlacesError(null)
      return
    }

    let cancelled = false
    setPlacesLoading(true)
    setPlacesError(null)

    const timer = window.setTimeout(async () => {
      try {
        const predictions = await fetchPlacePredictions(trimmed)
        if (!cancelled) {
          setPlacePredictions(predictions)
        }
      } catch (err) {
        if (!cancelled) {
          setPlacePredictions([])
          setPlacesError(err instanceof Error ? err.message : 'Could not load address suggestions')
        }
      } finally {
        if (!cancelled) {
          setPlacesLoading(false)
        }
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [isOpen, query])

  if (!isOpen) return null

  const q = query.trim().toLowerCase()
  const useGooglePlaces = q.length >= 2 && isGoogleMapsConfigured()
  const filtered = q
    ? locations.filter(
        (l) => l.name.toLowerCase().includes(q) || (l.description || '').toLowerCase().includes(q),
      )
    : locations

  const handlePick = async (label: string, placeId?: string) => {
    setResolvingPick(true)
    try {
      const coords = await resolveSearchCoordinates({ placeId, address: label })
      onSelect({
        label: coords?.formattedAddress || label,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        placeId,
      })
      onClose()
    } catch {
      onSelect({ label })
      onClose()
    } finally {
      setResolvingPick(false)
    }
  }

  const showGoogleResults = useGooglePlaces && (placesLoading || placePredictions.length > 0 || placesError)
  const showLocalResults = !useGooglePlaces && filtered.length > 0
  const showEmpty = !placesLoading && !loading && !showGoogleResults && !showLocalResults

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] animate-in fade-in duration-200"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 md:p-8">
        <div
          className="bg-white w-full h-full md:h-auto md:max-w-2xl md:max-h-[85vh] md:rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500 rounded-lg">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">Where to?</h3>
                <p className="text-xs md:text-sm text-gray-600">Search a city or destination</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/60 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="p-4 md:p-6 pb-2">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search city, area, or address..."
                className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-4 relative">
            {resolvingPick && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              </div>
            )}
            {loading && !useGooglePlaces ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              </div>
            ) : showGoogleResults ? (
              <div className="space-y-1">
                {placesLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-500" />
                  </div>
                )}

                {!placesLoading && placesError && (
                  <p className="text-center text-sm text-red-600 py-4">{placesError}</p>
                )}

                {!placesLoading &&
                  placePredictions.map((place) => (
                    <button
                      key={place.placeId}
                      type="button"
                      onClick={() => handlePick(place.description, place.placeId)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                        <MapPin className="h-5 w-5 text-primary-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">{place.mainText}</div>
                        {place.secondaryText && (
                          <div className="text-xs text-gray-500 truncate">{place.secondaryText}</div>
                        )}
                      </div>
                    </button>
                  ))}

                {!placesLoading && !placesError && placePredictions.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm font-semibold text-gray-700">No addresses found for "{query.trim()}"</p>
                    <p className="text-xs text-gray-500 mt-1">Try a different spelling or nearby city</p>
                  </div>
                )}
              </div>
            ) : showLocalResults ? (
              <div className="space-y-1">
                {!q && (
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1 pb-2">
                    Popular locations
                  </p>
                )}
                {filtered.map((loc, i) => (
                  <button
                    key={`${loc.name}-${i}`}
                    type="button"
                    onClick={() => handlePick(loc.name)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                      <MapPin className="h-5 w-5 text-primary-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">{loc.name}</div>
                      {loc.description && (
                        <div className="text-xs text-gray-500">{loc.description}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : showEmpty ? (
              <div className="text-center py-12">
                {q ? (
                  <>
                    <p className="text-sm font-semibold text-gray-700">No locations match "{query}"</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {isGoogleMapsConfigured()
                        ? 'Try a different search'
                        : 'Type at least 2 characters or configure Google Maps API key'}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">Start typing to search addresses</p>
                )}
                {q && (
                  <button
                    type="button"
                    onClick={() => handlePick(query.trim())}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors"
                  >
                    Use "{query.trim()}" anyway
                  </button>
                )}
              </div>
            ) : null}
          </div>

          <div className="border-t border-gray-200 p-4 md:p-6 bg-gray-50 flex items-center justify-between">
            <button
              type="button"
              onClick={() => handlePick('')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default LocationModal
